var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import './DropdownModal.scss';
import classNames from 'classnames';
import { OutsideClickWrapper } from '@ui/general';
import { getClosestButton, getClosestViewArea } from '@lib/popups';
import React, { useCallback, useEffect, useState } from 'react';
import { SonarModal } from './SonarModal';
function getContainerOffset(element, container, direction = 'top') {
    let currentElement = element;
    if (!currentElement) {
        return 0;
    }
    let totalOffset = 0;
    let scrollAdjustment = 0;
    while (currentElement !== container) {
        totalOffset += direction === 'top' ? currentElement.offsetTop : currentElement.offsetLeft;
        if (currentElement.offsetParent) {
            const offsetParent = currentElement.offsetParent;
            const scrollValue = direction === 'top' ? offsetParent.scrollTop : offsetParent.scrollLeft;
            if (scrollValue > 0) {
                scrollAdjustment += scrollValue;
            }
        }
        currentElement = currentElement.offsetParent;
        if (!currentElement) {
            break;
        }
    }
    const containerScroll = direction === 'top' ? container.scrollTop : container.scrollLeft;
    if (containerScroll > 0) {
        scrollAdjustment += containerScroll;
    }
    return totalOffset - scrollAdjustment;
}
function calculatePopoverPosition(position, triggerButton, container) {
    const triggerRect = triggerButton.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const topOffset = getContainerOffset(triggerButton, container);
    if (position === 'bottomLeft') {
        return {
            left: triggerRect.left - containerRect.left,
            top: topOffset - container.scrollHeight - triggerRect.height + 8
        };
    }
    if (position === 'bottomRight') {
        return {
            right: -triggerRect.width - (triggerRect.left - containerRect.left),
            top: topOffset - container.scrollHeight - triggerRect.height + 8
        };
    }
    if (position === 'topLeft') {
        return {
            bottom: container.scrollHeight - topOffset + triggerRect.height + 1,
            left: triggerRect.left - containerRect.left
        };
    }
    if (position === 'topRight') {
        return {
            bottom: container.scrollHeight - topOffset + triggerRect.height + 1,
            right: -triggerRect.width - (triggerRect.left - containerRect.left)
        };
    }
    return {};
}
export const DropdownModal = (_a) => {
    var { className, getPopupContainer = getClosestViewArea, onCancel, onClickOutside, onOk, popoverPosition = 'bottomLeft', style, targetNode } = _a, rest = __rest(_a, ["className", "getPopupContainer", "onCancel", "onClickOutside", "onOk", "popoverPosition", "style", "targetNode"]);
    const [open, setOpen] = useState(false);
    const [onCloseAction, setOnCloseAction] = useState();
    const handleClosing = useCallback((action) => {
        if (open) {
            setOnCloseAction(() => action);
            setOpen(false);
        }
    }, [open]);
    const onAfterClose = useCallback(() => {
        onCloseAction === null || onCloseAction === void 0 ? void 0 : onCloseAction();
        setOnCloseAction(undefined);
    }, [onCloseAction]);
    const handleOnClickOutside = useCallback((e) => {
        if (targetNode !== getClosestButton(e.target)) {
            if (onClickOutside) {
                onClickOutside(e);
            }
            else {
                handleClosing();
            }
        }
    }, [handleClosing, onClickOutside, targetNode]);
    const [customStyle, setCustomStyle] = useState(style !== null && style !== void 0 ? style : {});
    useEffect(() => {
        const handleClick = () => setOpen((value) => !value);
        targetNode === null || targetNode === void 0 ? void 0 : targetNode.addEventListener('click', handleClick);
        targetNode === null || targetNode === void 0 ? void 0 : targetNode.click();
        return () => {
            targetNode === null || targetNode === void 0 ? void 0 : targetNode.removeEventListener('click', handleClick);
        };
    }, [targetNode]);
    useEffect(() => {
        if (targetNode) {
            setCustomStyle((current) => {
                var _a, _b;
                return (Object.assign(Object.assign({}, current), calculatePopoverPosition(popoverPosition, (_a = getClosestButton(targetNode)) !== null && _a !== void 0 ? _a : targetNode, getClosestViewArea((_b = targetNode) !== null && _b !== void 0 ? _b : document.body))));
            });
        }
    }, [open, popoverPosition, targetNode]);
    const modalClassNames = classNames('sonar-modal-dropdown', {
        'sonar-modal-dropdown-bottom-left': popoverPosition === 'bottomLeft',
        'sonar-modal-dropdown-bottom-right': popoverPosition === 'bottomRight',
        'sonar-modal-dropdown-top-left': popoverPosition === 'topLeft',
        'sonar-modal-dropdown-top-right': popoverPosition === 'topRight'
    }, 'flex-inputs', className);
    return (React.createElement(OutsideClickWrapper, { onClickOutside: handleOnClickOutside },
        React.createElement(SonarModal, Object.assign({}, rest, { afterClose: onAfterClose, className: classNames('sonar-modal-dropdown', modalClassNames, className), getContainer: () => { var _a; return getPopupContainer((_a = targetNode) !== null && _a !== void 0 ? _a : document.body); }, onCancel: () => handleClosing(onCancel), onOk: () => handleClosing(onOk), open: open, rootClassName: 'sonar-modal-dropdown-root', style: customStyle }))));
};
